import React, { useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import IconButton from 'Components/Atoms/IconButton';
import { Search, Close, CheckBoxOutlined, IndeterminateCheckBoxOutlined } from 'Components/Atoms/Icons';
import Checkbox from 'Components/Atoms/Checkbox';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import './styles.scss';

type Option = { id: string; title: string; detail?: string; disabled?: boolean };
const fold = (value: string) =>
  value
    .toLocaleLowerCase('de')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss');

export default function CheckboxSelection({
  label,
  options,
  selected,
  disabled,
  onChange,
  selectedLabel = 'ausgewählt',
}: {
  label: string;
  selectedLabel?: string;
  options: Option[];
  selected: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchId] = useState(() => uniqueId('checkbox-selection-search-'));
  const closeSearch = () => {
    setQuery('');
    setSearchOpen(false);
    document.getElementById(`${searchId}-toggle`)?.focus();
  };
  const visible = options.filter((o) => fold(`${o.title} ${o.detail ?? ''} ${o.id}`).includes(fold(query.trim())));
  const available = visible.filter((o) => !o.disabled).map((o) => o.id);
  const changeVisible = (checked: boolean) =>
    onChange(checked ? [...new Set([...selected, ...available])] : selected.filter((id) => !available.includes(id)));
  return (
    <section className="checkbox-selection" aria-label={label}>
      <div className="checkbox-selection-toolbar">
        <IconButton
          id={`${searchId}-toggle`}
          className="checkbox-selection-action"
          size="small"
          aria-label={searchOpen ? `${label}-Suche schließen` : `${label} durchsuchen`}
          title={searchOpen ? 'Suche schließen' : 'Suchen'}
          aria-expanded={searchOpen}
          aria-controls={searchOpen ? searchId : undefined}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
        >
          {searchOpen ? <Close /> : <Search />}
        </IconButton>
        <Typography variant="text-3" translation={null} className="checkbox-selection-count" aria-live="polite">
          {selected.filter((id) => options.some((option) => option.id === id)).length}/{options.length} {selectedLabel}
          {query.trim() ? ` · ${visible.length} Treffer` : ''}
        </Typography>
        <div className="checkbox-selection-actions">
          <IconButton
            className="checkbox-selection-action"
            size="small"
            aria-label={query.trim() ? 'Treffer auswählen' : 'Alle auswählen'}
            title={query.trim() ? 'Treffer auswählen' : 'Alle auswählen'}
            disabled={disabled || !available.some((id) => !selected.includes(id))}
            onClick={() => changeVisible(true)}
          >
            <CheckBoxOutlined />
          </IconButton>
          <IconButton
            className="checkbox-selection-action"
            size="small"
            aria-label={query.trim() ? 'Treffer abwählen' : 'Alle abwählen'}
            title={query.trim() ? 'Treffer abwählen' : 'Alle abwählen'}
            disabled={disabled || !available.some((id) => selected.includes(id))}
            onClick={() => changeVisible(false)}
          >
            <IndeterminateCheckBoxOutlined />
          </IconButton>
        </div>
      </div>
      {searchOpen && (
        <TextField
          id={searchId}
          fullWidth
          autoFocus
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              event.preventDefault();
              closeSearch();
            }
          }}
          label={`${label} suchen`}
          labelTranslation={null}
          helperTextTranslation={null}
          InputLabelProps={{ style: { color: 'var(--color-text)' } }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className="checkbox-selection-list" role="group" aria-label={`${label} auswählen`} tabIndex={0}>
        {visible.map((o) => (
          <div key={o.id} className="checkbox-selection-option">
            <Checkbox
              translation={null}
              labelColor="textPrimary"
              label={o.title}
              checked={selected.includes(o.id)}
              disabled={disabled || o.disabled}
              onChange={(_, checked) =>
                onChange(checked ? [...new Set([...selected, o.id])] : selected.filter((id) => id !== o.id))
              }
            />
            {o.detail && (
              <Typography variant="text-3" block translation={null}>
                {o.detail}
              </Typography>
            )}
          </div>
        ))}
        {visible.length === 0 && (
          <Typography variant="text-3" translation={null}>
            Keine {label} gefunden.
          </Typography>
        )}
      </div>
    </section>
  );
}
